import React, { useState } from 'react';
import { Bookmark, Check, X } from 'lucide-react';
import { PLOT_THREAD_KINDS, PLOT_THREAD_KIND_LABELS } from '@xiaotianshu/shared';
import type { PlotThreadKind } from '@xiaotianshu/shared';
import type { EditorSelectionContext } from '../../../types';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import { errorMessage, notifyError } from '../../../utils/notify';
import '../styles/plotThreadModal.css';

/** 选中文字自动转成标题时的截断长度（只是给个能改的默认值，不追求完整） */
const TITLE_FROM_QUOTE_MAX = 30;
/** 弹窗里引文显示的截断长度：只用于确认"标的是哪一段" */
const QUOTE_PREVIEW_MAX = 160;

interface PlotThreadModalProps {
  bookId: string;
  /** 右键时的选中上下文：文本 + 章节 + 文档位置 */
  selection: EditorSelectionContext;
  onClose: () => void;
  /** 保存成功后回调（父层负责刷新数据、提示、跳转） */
  onCreated: (payload: { id: string; title: string; kind: PlotThreadKind }) => void;
}

/**
 * 从编辑器选中文字登记伏笔 / 线索。
 *
 * 为什么要有这个弹窗：伏笔此前只能在伏笔页手工登记，正文里读到"这里埋了个东西"
 * 时得先切页面、再手打一遍线索名和位置，成本高到会让人干脆不记。
 * 这里把登记动作挪到选中文字的当下，埋点章节固定取当前章（选中文字就在这一章），
 * 只需要选类型 + 写备注两件事。
 */
export function PlotThreadModal({ bookId, selection, onClose, onCreated }: PlotThreadModalProps) {
  const [kind, setKind] = useState<PlotThreadKind>('foreshadow');
  const [title, setTitle] = useState(() => selection.text.trim().slice(0, TITLE_FROM_QUOTE_MAX));
  const [detail, setDetail] = useState('');
  const [saving, setSaving] = useState(false);
  // 未按 Esc 关闭期间锁定遮罩点击，避免保存中误关丢输入
  const dismiss = useModalDismiss(saving ? () => { /* 保存中不响应 Esc */ } : onClose);

  const quote = selection.text.trim();
  const quotePreview = quote.length > QUOTE_PREVIEW_MAX
    ? `${quote.slice(0, QUOTE_PREVIEW_MAX)}…`
    : quote;

  const submit = async () => {
    if (!title.trim()) {
      notifyError('请填写线索标题');
      return;
    }
    setSaving(true);
    try {
      const created = await window.api.createPlotThread({
        bookId,
        kind,
        title: title.trim(),
        detail,
        // 埋点就是当前章节 —— 用户是在这一章的正文里选中这句话的
        plantedChapterId: selection.chapterId,
        quote: quote.slice(0, 2000),
        quoteStart: selection.from,
        quoteEnd: selection.to,
      });
      onCreated({ id: created.id, title: title.trim(), kind });
    } catch (err) {
      notifyError('登记伏笔失败：' + errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" {...dismiss}>
      <div className="modal-content ptm-modal">
        <h2 className="modal-title">添加伏笔 / 线索</h2>

        <div className="ptm-quote" title={quote}>
          <Bookmark size={12} className="ptm-quote-icon" />
          <span>{quotePreview}</span>
        </div>
        <p className="ptm-hint">
          埋点：第{selection.chapterNumber}章 {selection.chapterTitle}
        </p>

        <div className="ptm-field">
          <span className="ptm-label">类型</span>
          <div className="ptm-kinds" role="radiogroup" aria-label="线索类型">
            {PLOT_THREAD_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={kind === k}
                className={`ptm-kind ptm-kind-${k}${kind === k ? ' is-active' : ''}`}
                onClick={() => setKind(k)}
              >
                {PLOT_THREAD_KIND_LABELS[k]}
              </button>
            ))}
          </div>
        </div>

        <input
          className="modal-input"
          placeholder="线索标题"
          value={title}
          autoFocus
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !saving) void submit(); }}
          aria-label="线索标题"
        />
        <textarea
          className="modal-textarea"
          placeholder="备注（这段埋的是什么、打算怎么回收，可不填）"
          value={detail}
          rows={4}
          onChange={(e) => setDetail(e.target.value)}
          aria-label="线索备注"
        />

        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose} disabled={saving}>
            <X size={13} /> 取消
          </button>
          <button
            className="btn-confirm"
            onClick={() => void submit()}
            disabled={saving || !title.trim()}
            aria-label="确认添加伏笔"
          >
            <Check size={13} /> {saving ? '保存中…' : '添加'}
          </button>
        </div>
      </div>
    </div>
  );
}
